import * as d3 from "d3";
import * as React from "react";
import { Component } from "react";
import ViolinChart from "../charts/ViolinChart";
import Config from "../config";
import { Deployment } from "../domain/deployment";
import * as Dims from "../domain/dimension";
import { AllDimensionValues, DimensionValues, summaryKey } from "../domain/dimension";
import { I18nContext, useI18n } from "../domain/i18n";
import { Delta, linkToOrganization } from "../domain/organization";
import * as ReactUtils from "../lib/ReactUtils";
import { newRequestAnimationFrameQueue } from "../lib/requestAnimationFrame";
import * as VisUtils from "../lib/VisUtils";
import { Rating } from "./Rating";
import Tooltip from "./Tooltip";
const requestAnimationFrame = newRequestAnimationFrameQueue();

const ANIM_DURATION = 1000;

const rotate = (deg: number) => {
  const rot = "rotate(" + deg + "deg)";
  return {
    WebkitTransform: rot,
    MozTransform: rot,
    msTransform: rot,
    OTransform: rot,
    transform: rot,
  };
};

const rotationScale = d3.scale.linear().domain([-5, +5]).rangeRound([+40, -40]).clamp(true);

const deltaRotation = (delta: number) => rotate(rotationScale(delta));

// This interface is a subtype of 'OrganizationSurveyData'
export interface ViolinDatum {
  id: string; // NOT OrgId, can be DimKey
  name: string;
  legal_name: string;
  dimensions: AllDimensionValues;
  delta: { [dimKey: string]: Delta };
  rating: number;
}

interface ViolinCaptionTitleProps {
  rank: undefined | number;
  datum: ViolinDatum;
  maxTitleLength: number;
  showLink: boolean;
}

function ViolinCaptionTitle(props: ViolinCaptionTitleProps) {
  const I18n = useI18n();
  const { rank, datum, maxTitleLength, showLink } = props;
  const { name, legal_name: legalName } = datum;

  const className = "violin__caption-text";
  const titleText = VisUtils.shorten((rank != null ? rank + ". " : "") + name, maxTitleLength);

  if (showLink) {
    return (
      <a className={className + " link link--arrow"} href={linkToOrganization(I18n.locale, datum)} title={legalName}>
        {titleText}
      </a>
    );
  } else {
    return <span className={className}>{titleText}</span>;
  }
}

const formatDeltaValue = d3.format("+.1f");

interface ViolinCaptionProps {
  deployment: Deployment;
  datum: ViolinDatum;
  dimKey: Dims.DimKey;
  showLinkToDetailPage: boolean;
  showRating: boolean;
  showDelta: boolean;
  maxTitleLength: number;
  rank: undefined | number;
}

function ViolinCaption({
  deployment,
  datum,
  rank,
  dimKey,
  maxTitleLength,
  showRating,
  showDelta,
  showLinkToDetailPage,
}: ViolinCaptionProps) {
  const I18n = useI18n();
  const moreLink = (Config.titleMoreLinks(deployment) as any)[datum.id];
  const deltaPercent = datum.delta[dimKey] ? formatDeltaValue(datum.delta[dimKey].value) + "%" : undefined;

  return (
    <div className={"violin__caption key--" + datum.id + (showRating ? " is-rating-shown" : "")}>
      <i className="violin__caption-icon" />
      <ViolinCaptionTitle rank={rank} datum={datum} maxTitleLength={maxTitleLength} showLink={showLinkToDetailPage} />

      {moreLink && (
        <a className="violin__caption-more link link--arrow" href={moreLink.href}>
          {I18n.t("frontend.violin.more-links.repeated-evaluation")}
        </a>
      )}

      {showRating && (
        <div className="violin__rating">
          <Rating dimKey={dimKey} rating={datum.rating} />
        </div>
      )}

      {showDelta && datum.delta[dimKey] && (
        <span className="violin__caption-delta-icon trend-icon" style={deltaRotation(datum.delta[dimKey].value)} />
      )}
      {showDelta && datum.delta[dimKey] && (
        <span className="violin__caption-delta">
          {dimKey === "public_value"
            ? I18n.t("frontend.violin.delta", {
                percent: deltaPercent,
                year: datum.delta[dimKey].reference,
              })
            : deltaPercent}
        </span>
      )}
    </div>
  );
}

export interface ViolinPosition {
  left: number;
  top: number;
}

export interface ViolinMargin {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

const emptyViolinMargin: ViolinMargin = {
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
};

// TODO: Move to components/Tooltip once that file is ported to TypeScript
interface TooltipProps {
  position: any;
  value: string;
  classModifier: string;
  text: string;
}

interface ViolinProps {
  deployment: Deployment;
  className: string;

  width: number;
  height: number;
  position: ViolinPosition;
  margin: undefined | ViolinMargin;

  showSummaryDimAsOutline: undefined | boolean;

  organizationDatum: ViolinDatum;
  dims: Dims.Dimensions;

  rank: undefined | number;

  valueFormat?: (n: number) => string;
  showRating: undefined | boolean;
  showDelta: undefined | boolean;
  showAvgValueText: boolean;
  showAxisTicks: boolean;
  showLinkToDetailPage: undefined | boolean;
  enableTooltip: undefined | boolean;
  maxTitleLength: number;
  suppressTransitions: boolean;
  selected: boolean;

  onMouseEnter?: (d: ViolinDatum) => void;
  onMouseLeave?: (d: ViolinDatum) => void;
  onClick?: any;
}

interface ViolinState {
  isHighlighted: boolean;
  tooltip: null | TooltipProps;
}

export default function Violin(props: ViolinProps) {
  const I18n = useI18n();
  return <ViolinLegacy I18n={I18n} {...props} />;
}

class ViolinLegacy extends Component<ViolinProps & { I18n: I18nContext }, ViolinState> {
  state = {
    isHighlighted: false,
    tooltip: null as null | TooltipProps,
  };

  chart = ViolinChart()
    .xdomain([0, 7])
    .histogramBins(d3.range(0.5, 8, 1))
    .kdeLineInterpolation("monotone")
    .ydomain([0, 0.7])
    .showKdePoints(false)
    .showHistogram(false)
    .dimensionsOrdering(["public_value", "ethics", "benefit", "social_cohesion", "quality_of_life"])
    .axisLabels({ min: "", max: "" });

  nodeSelection: null | d3.Selection<any> = null;
  rootRef = (ref: null | Element): void => {
    this.nodeSelection = ref ? d3.select(ref) : null;
  };

  onMouseEnter = () => {
    const { organizationDatum, onMouseEnter } = this.props;
    this.setState({ isHighlighted: true });
    if (onMouseEnter) {
      onMouseEnter(organizationDatum);
    }
  };

  onMouseLeave = () => {
    const { organizationDatum, onMouseLeave } = this.props;
    this.setState({ isHighlighted: false });
    if (onMouseLeave) {
      onMouseLeave(organizationDatum);
    }
  };

  // requestAnimationFrame to call 'adjustDimensionsOpacity'. Is cancelled when the
  // component is unmounted.
  adjustDimensionsOpacityRAF: undefined | number = undefined;
  adjustDimensionsOpacity = () => {
    this.adjustDimensionsOpacityRAF = undefined;

    const { dims } = this.props;
    const selectedDim = this.getSelectedDim();
    const maxw = d3.max(d3.values(Dims.asObject(dims).weights));

    // XXX: The 'this' function argument is fake, just for TypeScript.
    // See https://github.com/Microsoft/TypeScript/pull/6739
    if (this.nodeSelection) {
      this.nodeSelection.selectAll(".violin__path").each(function (this: Element, d) {
        const dimKey = d.key;
        const opacity = selectedDim != null ? (dimKey === selectedDim ? 1 : 0) : Dims.weight(dims, dimKey) / maxw;
        d3.select(this).style("fill-opacity", opacity);
      });
    }
  };

  shouldComponentUpdate(nextProps: ViolinProps, nextState: ViolinState): boolean {
    return (
      ReactUtils.anyPropChanged(nextProps, this.props, [
        "selected",
        "position.top",
        "position.left",
        "width",
        "height",
        "margin",
        "rank",
        "showAvgValueText",
        "organizationDatum",
        "rating",
        "year",
      ]) ||
      ReactUtils.anyPropChanged(nextState, this.state, ["tooltip", "isHighlighted"]) ||
      Dims.changed(nextProps.dims, this.props.dims, { ignoreLocks: true })
    );
  }

  showTooltip(tooltip: null | TooltipProps): void {
    this.setState({ tooltip });
  }

  getSelectedDim(): null | Dims.DimKey {
    const { dims } = this.props;
    return Dims.highlighted(dims) || Dims.selected(dims);
  }

  getDimensionValues() {
    const { organizationDatum } = this.props;
    const dim = this.getSelectedDim();

    if (dim != null) {
      return organizationDatum.dimensions.filter((d) => d.key === dim);
    } else {
      return organizationDatum.dimensions;
    }
  }

  updateChart() {
    const {
      I18n,
      width,
      height,
      margin = emptyViolinMargin,
      showSummaryDimAsOutline,
      enableTooltip,
      showAvgValueText,
      showAxisTicks,
      valueFormat,
    } = this.props;
    const { isHighlighted } = this.state;

    const dimensionValues = this.getDimensionValues();
    const selectedDim = this.getSelectedDim();

    this.chart = this.chart
      .width(width)
      .height(height)
      .margin({
        top: margin.top || 0,
        right: margin.right || 0,
        bottom: margin.bottom || 0,
        left: margin.left || 0,
      })
      .selectedDim(selectedDim)
      .useOutline(showSummaryDimAsOutline ? (d: DimensionValues) => d.key === summaryKey : undefined)
      .showAvgValueText(isHighlighted || showAvgValueText)
      .showAxisTicks(isHighlighted || showAxisTicks)
      .valueFormat(Config.valueFormat || valueFormat);

    if (enableTooltip) {
      this.chart.on("hover", (dd: any) => {
        if (dd != null) {
          const { value, position, dimKey, x } = dd;
          if (x > 0 && x < 7) {
            this.showTooltip({
              position,
              value: "" + value,
              classModifier: "dim--" + dimKey,
              text: I18n.t("frontend.violin.rating", { count: value }),
            });
          }
        } else {
          this.showTooltip(null);
        }
      });
    }

    if (this.nodeSelection) {
      this.nodeSelection.select(".violin__svg").datum(dimensionValues).call(this.chart);
      this.nodeSelection
        .transition()
        .duration(ANIM_DURATION)
        .style("opacity", 1)
        .style("top", this.props.position.top + "px")
        .style("left", this.props.position.left + "px");
    }

    if (this.adjustDimensionsOpacityRAF === undefined) {
      this.adjustDimensionsOpacityRAF = requestAnimationFrame(this.adjustDimensionsOpacity);
    }
  }

  componentDidMount() {
    this.updateChart();
    this.componentWillEnter(() => {}); // eslint-disable-line @typescript-eslint/no-empty-function
  }

  componentDidUpdate() {
    this.updateChart();
  }

  componentWillEnter(done: () => void): void {
    if (this.nodeSelection) {
      const { suppressTransitions, position } = this.props;

      if (suppressTransitions) {
        this.nodeSelection
          .style("top", position.top + "px")
          .style("left", position.left + "px")
          .transition();
      } else {
        this.nodeSelection
          .style("opacity", 0)
          .style("top", position.top + "px")
          .style("left", position.left + "px")
          .transition()
          .duration(ANIM_DURATION)
          .style("opacity", 1);
      }
    }

    setTimeout(done, ANIM_DURATION);
  }

  componentWillLeave(done: () => void): void {
    if (this.nodeSelection) {
      const { suppressTransitions } = this.props;

      if (!suppressTransitions) {
        this.nodeSelection.transition().duration(ANIM_DURATION).style("opacity", 0);
      }
    }

    setTimeout(done, ANIM_DURATION);
  }

  componentWillUnmount() {
    if (this.adjustDimensionsOpacityRAF) {
      requestAnimationFrame.cancel(this.adjustDimensionsOpacity);
    }
  }

  // This function is not used anywhere!
  // TODO-REMOVE
  onSelectNode(nodeId: any): void {
    this.props.onClick(nodeId);
  }

  render() {
    const {
      deployment,
      className,
      selected,
      dims,
      showLinkToDetailPage,
      showRating,
      showDelta,
      maxTitleLength,
      rank,
      height,
      width,
    } = this.props;
    const { tooltip } = this.state;
    const datum = this.props.organizationDatum;

    const rootClassName =
      "violin key--" + datum.id + (selected ? " is--selected" : "") + (className ? " " + className : "");

    return (
      <div
        ref={this.rootRef}
        className={rootClassName}
        data-key={datum.id} // Why is this needed? Found it, in IntroViolinGrid!
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
      >
        <ViolinCaption
          deployment={deployment}
          datum={datum}
          dimKey={Dims.selected(dims) || summaryKey}
          showLinkToDetailPage={showLinkToDetailPage || false}
          showRating={showRating || false}
          showDelta={showDelta || false}
          maxTitleLength={maxTitleLength}
          rank={rank}
        />
        <svg height={height} width={width} className="violin__svg" />
        {tooltip && <Tooltip {...tooltip} />}
      </div>
    );
  }
}
